import { endViewPagePerformanceModeVisit } from '@confluence/page-context/entry-points/viewPagePerformanceModeStore.ts';
import type {
	useHistory,
	useMatchRoute,
} from '@confluence/route-manager/entry-points/RouteState.ts';

type Visit = {
	id: number;
	contentId: string;
	routerHistory: NonNullable<ReturnType<typeof useHistory>>;
	stopListening: () => void;
	hasEnded: boolean;
	cancelNotification?: () => void;
};

let currentVisit: Visit | null = null;
let nextVisitId = 0;

const endVisit = (visit: Visit): void => {
	if (currentVisit !== visit) {
		return;
	}
	currentVisit = null;
	visit.hasEnded = true;
	visit.stopListening();
	visit.cancelNotification?.();
	endViewPagePerformanceModeVisit(visit.contentId);
};

const hasHistoricalVersion = (search: string): boolean => {
	const query = new URLSearchParams(search);
	return query.has('versionOverride') || query.has('pageVersion');
};

// Notification ownership belongs to the page visit, rather than to a renderer instance.
// Keep listening after a renderer unmounts so queued flags cannot arrive on another page.
export const trackViewPagePerformanceModeVisit = (
	contentId: string,
	history: ReturnType<typeof useHistory>,
	matchRoute: ReturnType<typeof useMatchRoute>,
): Visit | null => {
	if (!history) {
		return null;
	}
	if (currentVisit?.contentId === contentId && currentVisit.routerHistory === history) {
		return currentVisit;
	}
	if (currentVisit) {
		endVisit(currentVisit);
	}
	const currentRoute = matchRoute(
		`${history.location.pathname}${history.location.search}${history.location.hash}`,
	);
	if (
		currentRoute?.params.contentId !== contentId ||
		hasHistoricalVersion(history.location.search)
	) {
		return null;
	}
	const visit: Visit = {
		id: nextVisitId++,
		contentId,
		routerHistory: history,
		stopListening: () => {},
		hasEnded: false,
	};
	currentVisit = visit;
	visit.stopListening = history.listen((location) => {
		const nextRoute = matchRoute(`${location.pathname}${location.search}${location.hash}`);
		// Historical versions keep the page route but unmount performance mode.
		if (
			nextRoute?.params.contentId !== contentId ||
			nextRoute?.name !== currentRoute?.name ||
			hasHistoricalVersion(location.search)
		) {
			endVisit(visit);
		}
	});
	return visit;
};

export const clearViewPagePerformanceModeVisitForTesting = (): void => {
	if (currentVisit) {
		endVisit(currentVisit);
	}
};
